import { Button, Card, Flex, Heading } from '@utilitywarehouse/hearth-react';
import { FormikProps, useFormikContext } from 'formik';
import { FormEvent, MutableRefObject, Ref } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { FormikPersist } from '@components/FormikPersist';
import { PaperBillingModal } from '@components/PaperBillingModal';
import { ScrollToError } from '@components/ScrollToError';
import { DETAILS } from '@constants/formNames';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import { filter as sensitiveFields } from '@features/Remote/Interaction/filter';
import { CustomerDocuments } from '@features/details/Details/CustomerDocuments';
import { RenewalCapture } from '@features/details/Details/RenewalCapture';
import {
  AttributionDetails,
  BundleQuoteFragment,
  PartnerConnector,
  ProductType,
} from '@generated/gql/graphql';
import { useOTSFlags } from '@hooks/useOTSFlags';
import { useTelcoLegal } from '@hooks/useTelcoLegal.ts';
import { YourDetailsFormValues } from '@src/types/yourDetails';
import { useUserRole } from '@utils/useUserRole';

import { BroadbandInstallation } from '../Details/BroadbandInstallation/BroadbandInstallation';
import { useIsBroadbandInstallationEnabled } from '../Details/BroadbandInstallation/hooks/useIsBroadbandInstallationEnabled';
import { AboutYou } from './AboutYou';
import { EmergencyCalls } from './Broadband/EmergencyCalls';
import { DirectDebit } from './DirectDebit';
import { OneTouchSwitching } from './OneTouchSwitching';
import { ReferralLegal } from './ReferralLegal';
import { YourAccount } from './YourAccount';
import { orderedFields } from './orderedFields';
import './styles.scss';

interface DetailsFormProps {
  connectors: Nullable<PartnerConnector[]>;
  attributionDetails: Nullable<AttributionDetails>;
  isLoading: boolean;
  productTypes: ProductType[];
  selectedQuotes: BundleQuoteFragment[];
  isFixedSelected: boolean;
  isDigitalHomePhoneSelected: boolean;
  ref?: Ref<HTMLFormElement>;
}

const DetailsForm = ({
  isLoading,
  selectedQuotes,
  productTypes,
  connectors,
  attributionDetails,
  isFixedSelected,
  isDigitalHomePhoneSelected,
  ref,
}: DetailsFormProps) => {
  // typecast the forwarded ref as a MutableRefObject, so it can be used
  // in this component to submit the form
  const formRef = ref as MutableRefObject<HTMLFormElement>;
  const paperBilling = useDialog(DialogID.PAPER_BILLING);
  const { isCorporatePartner, isSales } = useUserRole();

  const {
    values,
    isSubmitting,
    handleSubmit: onSubmit,
  }: FormikProps<YourDetailsFormValues> = useFormikContext();

  const { canSwitch, isSwitch } = useOTSFlags(productTypes, values);

  const { hasTelcoService } = useTelcoLegal(selectedQuotes, values);
  const hasCBCService = selectedQuotes.some(
    (quote) => quote.product.type === ProductType.CashbackCard
  );
  const showCustomerDocumentsSection =
    (isSales || isCorporatePartner) && (hasTelcoService || hasCBCService);
  const isBroadbandInstallationEnabled =
    useIsBroadbandInstallationEnabled(selectedQuotes);
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (isLoading || isSubmitting) {
      event.preventDefault();
      return;
    }

    onSubmit(event);
  };

  return (
    <>
      <FormSync />
      <FormikPersist name={DETAILS} exclude={sensitiveFields} />

      <form
        className="details__form"
        ref={ref}
        onSubmit={handleSubmit}
        noValidate
      >
        <Card direction="column" spacing="xl">
          <Heading className="details__heading" size="md" as="h2">
            About you
          </Heading>
          <AboutYou />
        </Card>
        {showCustomerDocumentsSection && (
          <Card direction="column" spacing="xl">
            <Heading className="details__heading" size="md" as="h2">
              Email customer documents
            </Heading>
            <CustomerDocuments
              selectedQuotes={selectedQuotes}
              showEeccContract={hasTelcoService}
              showCbcTerms={hasCBCService}
            />
          </Card>
        )}
        <Card direction="column" spacing="xl">
          <Heading className="details__heading" size="md" as="h2">
            Your account
          </Heading>
          <YourAccount
            hasCbc={productTypes.includes(ProductType.CashbackCard)}
          />
        </Card>
        <Card direction="column" spacing="xl">
          <Heading className="details__heading" size="md" as="h2">
            Your direct debit
          </Heading>
          <DirectDebit />
        </Card>
        {canSwitch && (
          <Card direction="column" spacing="xl" id="otsSection">
            <OneTouchSwitching />
            {isBroadbandInstallationEnabled && <BroadbandInstallation />}
            {isDigitalHomePhoneSelected && <EmergencyCalls />}
          </Card>
        )}
        <Card direction="column" spacing="xl">
          <Heading className="details__heading" size="md" as="h2">
            Terms and conditions
          </Heading>
          <ReferralLegal
            connectors={connectors}
            attributionDetails={attributionDetails}
            isSwitch={isSwitch}
            selectedQuotes={selectedQuotes}
            isFixedSelected={isFixedSelected}
          />
        </Card>
        <RenewalCapture
          selectedProducts={selectedQuotes.map((q) => q.product.type)}
        />
        <Flex justifyContent="end">
          <span>
            <Button
              variant="solid"
              colorScheme="highlight"
              disabled={isLoading || isSubmitting}
              type="submit"
              className="details__submit"
              data-testid="details-submit-order"
            >
              Confirm order
            </Button>
          </span>
        </Flex>
        <ScrollToError
          orderedFields={orderedFields}
          ignoreFields={['rlTermsAccept']}
        />
        <PaperBillingModal
          dialogId={paperBilling.id}
          energyOnly={productTypes.every((type) => type === ProductType.Energy)}
          onClose={() => {
            paperBilling.setOpen(false);
            document
              ?.querySelector('[name="aboutEmail"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          onConfirm={() => formRef?.current?.requestSubmit()}
        />
      </form>
    </>
  );
};
export default DetailsForm;
